import { useState, useEffect, useRef } from 'react';
import axios from '../../../lib/axios';
import { ADMIN_ORDER_FIND_MANY } from '../../../lib/endpoints/order';
import { DEFAULT_FIRST_GET_DATA_AMOUNT, DEFAULT_ROWS_PER_PAGE } from '../../../config-global';
import { OrderStatusEnum } from '../../../enums/orderStatus';
import {
  convertOrderStatusCodeToOrderStatusEnum,
  convertOrderStatusEnumToStatusCode,
} from '../../../utils/convertOrderStatusValue';
import { OrderModel } from '../../../models/order';
import { MerChantModel } from '../../../models/merChantModel';
import { MERCHANT_STORE_FIND_MANY } from '../../../lib/endpoints/merchant';
import { useDispatch, useSelector } from 'react-redux';
import { closeSound, removeNewOrderId } from '../../../store/notificationSlice';
import { RootState } from '../../../store';
import { ShipmentMethodEnum } from '../../../enums/shipmentMethod';
// ---------------------------------------------------

const useOrderController = () => {
  const dispatch = useDispatch();
  // ----------------- variables ------------------
  const [data, setData] = useState<OrderModel[]>([]);
  const [merchantData, setMerchantData] = useState<MerChantModel[]>([]);
  const [merchantValue, setMerchantValue] = useState<string>('All');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingGetData, setLoadingGetData] = useState<boolean>(false);
  const [loadingFetchDataMore, setLoadingFetchDataMore] = useState<boolean>(false);
  const searchRef = useRef<HTMLInputElement>(null!);
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedId, setSelectedId] = useState<string>(null!);
  const [selectedData, setSelectedData] = useState<any>(null!);
  const [currentOrderStatus, setCurrentOrderStatus] = useState<OrderStatusEnum>(
    OrderStatusEnum.CURRENT_ORDER
  );

  const notification = useSelector((state: RootState) => state?.notificationSlice);
  const roleAdmin = useSelector((state: RootState) => state?.auth?.data?.role?.role_type);
  const assignMerchantToAdmin = useSelector((state: RootState) => state?.auth?.data?.merchants);

  // ----------------- functions --------------------
  const handleGetDataByOrderId = async () => {
    try {
      setLoadingGetData(true);
      const res = await axios.post(ADMIN_ORDER_FIND_MANY, {
        filter: {
          orderNo: searchRef.current.value,
        },
      });
      const newData: OrderModel[] = res?.data;
      if (newData?.length > 0) {
        setCurrentOrderStatus(
          convertOrderStatusCodeToOrderStatusEnum(newData[0]?.status?.code) as OrderStatusEnum
        );
      }
      setData(newData);
    } catch (err) {
    } finally {
      setLoadingGetData(false);
    }
  };

  const handleGetData = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
          OrderStatusEnum.CURRENT_ORDER
        )}`
      );
      setData(res?.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleGetDataByOrderStatus = async (getOrderStatus: OrderStatusEnum) => {
    try {
      setLoadingGetData(true);
      const passOrderStatus: OrderStatusEnum =
        getOrderStatus === OrderStatusEnum.Rider_Getting_Order ||
        getOrderStatus === OrderStatusEnum.READY_TO_PICKUP
          ? OrderStatusEnum.To_Ship
          : getOrderStatus;
      // console.log(
      //   `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
      //     passOrderStatus
      //   )}`
      // );
      let res: any = null!;
      // console.log(
      //   `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
      //     passOrderStatus
      //   )}`
      // );
      // console.log('merchantValue=', merchantValue);
      if (merchantValue === 'All') {
        res = await axios.post(
          `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
            passOrderStatus
          )}`
        );
      } else {
        res = await axios.post(
          `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
            passOrderStatus
          )}`,
          {
            filter: {
              merchantId: merchantValue,
            },
          }
        );
      }

      if (getOrderStatus === OrderStatusEnum.Rider_Getting_Order) {
        setData(
          res?.data?.filter(
            (item: OrderModel) => item?.rider && item?.orderMethod === ShipmentMethodEnum.DELIVERY
          )
        );
      } else if (getOrderStatus === OrderStatusEnum.To_Ship) {
        setData(
          res?.data?.filter(
            (item: OrderModel) => !item?.rider && item?.orderMethod === ShipmentMethodEnum.DELIVERY
          )
        );
      } else if (getOrderStatus === OrderStatusEnum.READY_TO_PICKUP) {
        setData(
          res?.data?.filter((item: OrderModel) => item?.orderMethod === ShipmentMethodEnum.PICK_UP)
        );
      } else {
        setData(res?.data);
      }
    } catch (err) {
      // console.log('error=', err);
    } finally {
      setLoadingGetData(false);
    }
  };

  const handleRefetchData = async () => {
    try {
      handleGetDataByOrderStatus(currentOrderStatus);
    } catch (err) {
    } finally {
    }
  };

  const handleGetMerchantData = async () => {
    try {
      const merchantRes = await axios.get(MERCHANT_STORE_FIND_MANY);
      if (merchantRes?.data?.length > 0) {
        const matchingMerchants = merchantRes.data.filter((merchant: any) =>
          assignMerchantToAdmin?.includes(merchant?.id)
        );

        if (roleAdmin === 'SUPER_ADMIN') {
          setMerchantData(merchantRes?.data);
        } else if (roleAdmin === 'ADMIN') {
          if (matchingMerchants.length > 0) {
            setMerchantData(matchingMerchants);
            setMerchantValue(matchingMerchants[0]?.id);
          }
        }
      }
    } catch (err) {
    } finally {
    }
  };

  const handleFilterDataByItemCode = () => {};

  const handleCurrentOrderStatusChange = (newValue: OrderStatusEnum) => {
    setCurrentOrderStatus(newValue);
    handleGetDataByOrderStatus(newValue);
    setCurrentPage(1);
  };

  const handleOpenMenu = (event: any, getId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedId(getId);
    // setSelectedData(data?.filter((item: any) => item?.id === getId)[0]);
  };

  const handleFetchDataMore = () => {};

  const handlePageChange = (event: any, newPage: any) => {
    if (newPage === countPagination) {
      if (!loadingFetchDataMore) {
        handleFetchDataMore();
      }
    }
    setCurrentPage(newPage);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleOpenForm = () => {
    setOpenMenu(null);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedData(null!);
  };

  const handleCountTotalQuantityInCart = (cartItems: any) => {
    let totalQuantity = 0;
    for (let i = 0; i < cartItems.length; i++) {
      totalQuantity += cartItems[i].quantity;
    }
    return totalQuantity;
  };

  const handleClickViewData = () => {
    setOpenForm(true);
    setSelectedData(data?.filter((item: any) => item?.id === selectedId)[0]);
    setOpenMenu(null);
    dispatch(removeNewOrderId(selectedId));
    dispatch(closeSound());
  };

  const filterOrderDataByBranch: OrderModel[] = data?.filter(
    (data: OrderModel) =>
      merchantData
        ?.filter((subItem) => subItem?.id === data?.merchantId)
        ?.filter((branch) => branch?.id === merchantValue).length > 0
  );

  const sourceData =
    merchantValue === null || merchantValue === 'All' ? data : filterOrderDataByBranch;

  const rows = sourceData.slice(
    (currentPage - 1) * DEFAULT_ROWS_PER_PAGE,
    currentPage * DEFAULT_ROWS_PER_PAGE
  );

  const countPagination = Math.ceil(sourceData.length / DEFAULT_ROWS_PER_PAGE);

  //   const isSearchNotFound: boolean = !data.length;

  // ----------------- useEffect --------------------

  useEffect(() => {
    if (
      notification?.data &&
      notification?.data?.status?.code === convertOrderStatusEnumToStatusCode(currentOrderStatus)
    ) {
      setData([notification?.data, ...data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification?.data]);

  useEffect(() => {
    handleGetData();
    handleGetMerchantData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    loading,
    rows,
    countPagination,
    openForm,
    searchRef,
    handleFilterDataByItemCode,
    currentPage,
    loadingFetchDataMore,
    handleOpenMenu,
    handlePageChange,
    openMenu,
    handleCloseMenu,
    handleOpenForm,
    handleCloseForm,
    handleCountTotalQuantityInCart,
    handleClickViewData,
    selectedData,
    currentOrderStatus,
    handleCurrentOrderStatusChange,
    loadingGetData,
    handleRefetchData,
    handleGetDataByOrderId,
    // isSearchNotFound,
    merchantData,
    merchantValue,
    handleSelectedMerchant: (e: any) => setMerchantValue(e.target.value),
    roleAdmin,
    sourceData,
  };
};
export default useOrderController;
